import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Upload, FileText, Trash2, Loader2, Volume2 } from 'lucide-react';
import { VOICE_TONES, getTone, speak, stopSpeaking, speechSupported } from '../lib/useVoice';

const AVATARS = ['🤖', '🧠', '🔍', '✨', '🏃', '💻', '🍳', '🧘', '🚀', '🎨', '📊', '🤝', '🌟', '🧪', '💡', '🎯'];
const COLORS = ['#0e9488', '#0ea5e9', '#f97316', '#10b981', '#8b5cf6', '#ef4444', '#14b8a6', '#eab308'];

export default function AgentDialog({ open, onOpenChange, agent, catalog, providers, skills, onSaved }) {
  const editing = !!agent?.agent_id;
  const [form, setForm] = useState({ name: '', description: '', persona: '', avatar: '🤖', color: '#0e9488', provider: 'openai', model: 'gpt-5.4', skills: [], voice: '', voice_rate: 1.0 });
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (open) {
      setForm(agent?.agent_id ? {
        name: agent.name, description: agent.description || '', persona: agent.persona || '',
        avatar: agent.avatar || '🤖', color: agent.color || '#0e9488',
        provider: agent.provider || 'openai', model: agent.model || 'gpt-5.4', skills: agent.skills || [], voice: agent.voice || '', voice_rate: agent.voice_rate || 1.0,
      } : { name: '', description: '', persona: '', avatar: '🤖', color: '#0e9488', provider: 'openai', model: 'gpt-5.4', skills: [], voice: '', voice_rate: 1.0 });
      if (agent?.agent_id) loadFiles(agent.agent_id);
      else setFiles([]);
    }
  }, [open, agent]);

  const loadFiles = async (id) => {
    try { const res = await api.get(`/files?scope=agent&agent_id=${id}`); setFiles(res.data); } catch (e) {}
  };

  const modelsForProvider = catalog?.[form.provider] || [{ id: form.model, label: form.model }];

  const save = async () => {
    if (!form.name.trim()) { toast.error('Give your agent a name'); return; }
    setBusy(true);
    try {
      let res;
      if (editing) res = await api.put(`/agents/${agent.agent_id}`, form);
      else res = await api.post('/agents', form);
      toast.success(editing ? 'Agent updated' : 'Agent created');
      onSaved(res.data);
      onOpenChange(false);
    } catch (e) { toast.error('Could not save agent'); } finally { setBusy(false); }
  };

  const uploadKnowledge = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !agent?.agent_id) { if (!agent?.agent_id) toast.error('Save the agent first, then add knowledge'); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/files?scope=agent&agent_id=${agent.agent_id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Knowledge added');
      loadFiles(agent.agent_id);
    } catch (err) { toast.error('Upload failed'); }
  };

  const removeFile = async (id) => { await api.delete(`/files/${id}`); loadFiles(agent.agent_id); };
  const toggleSkill = (id) => setForm((f) => ({ ...f, skills: f.skills.includes(id) ? f.skills.filter((x) => x !== id) : [...f.skills, id] }));

  const previewVoice = () => {
    const tone = getTone(form.voice);
    const sample = tone ? tone.sample : "Hi, I'm your agent. This is how I'll sound on our calls.";
    stopSpeaking();
    speak(sample, { voice: form.voice, rate: form.voice_rate });
  };

  const speedLabel = (r) => (r < 0.9 ? 'Slower' : r > 1.1 ? 'Faster' : 'Normal');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{editing ? 'Edit agent' : 'Build a new agent'}</DialogTitle>
          <DialogDescription>Give it a personality, pick its brain, and wire up skills.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-14 w-14 rounded-2xl grid place-items-center text-2xl" style={{ background: form.color + '22' }}>{form.avatar}</div>
            <div className="flex-1"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Research Buddy" className="h-10 rounded-xl mt-1" data-testid="agent-name-input" /></div>
          </div>
          <div>
            <Label className="text-xs text-[hsl(var(--muted-foreground))]">Avatar</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">{AVATARS.map((a) => <button key={a} onClick={() => setForm({ ...form, avatar: a })} className={`h-9 w-9 rounded-xl text-lg grid place-items-center transition-colors ${form.avatar === a ? 'ring-2 ring-[hsl(var(--primary))] bg-[hsl(var(--accent))]' : 'bg-[hsl(var(--secondary))]'}`}>{a}</button>)}</div>
          </div>
          <div>
            <Label className="text-xs text-[hsl(var(--muted-foreground))]">Accent color</Label>
            <div className="flex gap-2 mt-1.5">{COLORS.map((c) => <button key={c} onClick={() => setForm({ ...form, color: c })} className={`h-7 w-7 rounded-lg transition-transform ${form.color === c ? 'ring-2 ring-offset-2 ring-[hsl(var(--foreground))] scale-110' : ''}`} style={{ background: c }} />)}</div>
          </div>
          <div><Label>Short description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="One line about what it does" className="h-10 rounded-xl mt-1" data-testid="agent-description-input" /></div>
          <div><Label>Persona / instructions</Label><Textarea value={form.persona} onChange={(e) => setForm({ ...form, persona: e.target.value })} placeholder="You are a witty travel planner who..." className="min-h-[90px] rounded-xl mt-1" data-testid="agent-persona-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Provider</Label>
              <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v, model: (catalog?.[v]?.[0]?.id) || form.model })}>
                <SelectTrigger className="h-10 rounded-xl mt-1" data-testid="agent-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(providers || {}).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Model</Label>
              <Select value={form.model} onValueChange={(v) => setForm({ ...form, model: v })}>
                <SelectTrigger className="h-10 rounded-xl mt-1" data-testid="agent-model-select"><SelectValue /></SelectTrigger>
                <SelectContent>{modelsForProvider.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {['openrouter', 'custom'].includes(form.provider) && <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">This provider needs credentials in Settings to run. Otherwise the agent will ask you to add them.</p>}

          <div>
            <Label>Voice</Label>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1.5">How this agent sounds on voice calls and read-aloud.</p>
            <div className="flex items-center gap-2">
              <Select value={form.voice || 'default'} onValueChange={(v) => setForm({ ...form, voice: v === 'default' ? '' : v })}>
                <SelectTrigger className="h-10 rounded-xl flex-1" data-testid="agent-voice-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">System default</SelectItem>
                  {VOICE_TONES.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.label} — {t.hint}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" onClick={previewVoice} disabled={!speechSupported()} className="h-10 rounded-xl shrink-0 gap-1.5" data-testid="agent-voice-preview-button">
                <Volume2 className="h-4 w-4" /> Preview
              </Button>
            </div>
            {!speechSupported() && <p className="text-[11px] text-amber-700 mt-1.5">Voice preview needs a Chromium browser (Chrome/Edge).</p>}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Voice speed</Label>
              <span className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="agent-voice-rate-value">{speedLabel(form.voice_rate)} · {form.voice_rate.toFixed(2)}x</span>
            </div>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2.5">Nudge how fast this agent talks so calls match your energy.</p>
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-[hsl(var(--muted-foreground))] shrink-0">Slower</span>
              <Slider
                value={[form.voice_rate]}
                min={0.7} max={1.4} step={0.05}
                onValueChange={(v) => setForm({ ...form, voice_rate: v[0] })}
                className="flex-1"
                data-testid="agent-voice-rate-slider"
              />
              <span className="text-[11px] text-[hsl(var(--muted-foreground))] shrink-0">Faster</span>
            </div>
          </div>

          {skills?.length > 0 && (
            <div>
              <Label>Skills</Label>
              <div className="space-y-1.5 mt-1.5">
                {skills.map((s) => (
                  <div key={s.skill_id} className="flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] p-2.5">
                    <Badge variant="secondary" className="text-[10px] font-mono">{s.kind}</Badge>
                    <span className="text-sm flex-1 truncate">{s.name}</span>
                    <Switch checked={form.skills.includes(s.skill_id)} onCheckedChange={() => toggleSkill(s.skill_id)} data-testid={`agent-skill-toggle-${s.skill_id}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label>Knowledge (.md files)</Label>
            <label className="mt-1.5 flex items-center justify-center gap-2 rounded-xl border border-dashed border-[hsl(var(--border))] py-3 text-sm text-[hsl(var(--muted-foreground))] cursor-pointer hover:bg-[hsl(var(--secondary))] transition-colors">
              <Upload className="h-4 w-4" /> {editing ? 'Upload a markdown file' : 'Save agent first to add knowledge'}
              <input type="file" accept=".md,.markdown,.txt" className="hidden" onChange={uploadKnowledge} disabled={!editing} data-testid="agent-knowledge-upload" />
            </label>
            {files.map((f) => (
              <div key={f.file_id} className="flex items-center gap-2 mt-1.5 text-sm"><FileText className="h-4 w-4" /><span className="flex-1 truncate">{f.name}</span><button onClick={() => removeFile(f.file_id)}><Trash2 className="h-3.5 w-3.5 text-[hsl(var(--destructive))]" /></button></div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} className="rounded-xl" style={{ background: 'hsl(var(--primary))' }} data-testid="agent-builder-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Save changes' : 'Create agent')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
